import React from 'react';
import { Flex } from '@applatform/design-system';
import Container from 'components/V1/Container/Container';
import Icon from 'components/V1/Icon/Icon';
import {
  MediumAndAbove,
  SmallAndBelow,
} from 'components/V1/MediaQuery/MediaQuery';
import UserMenu from '../UserMenu/UserMenu';
import AvatarButton from '../TopBar/AvatarButton/AvatarButton';
import MarketplaceName from './MarketplaceName/MarketplaceName';
import { ReactComponent as UserIcon } from 'icons/user.svg';
import { ReactComponent as ZaiMark } from 'icons/zai-payments-icon.svg';

interface TopBarV2Props {
  readonly mobileNavButton: React.ReactNode;
}

/**
 * The v2 topbar. Two arrangements of the same four pieces:
 *
 *   desktop  [ name ] .......................... [ avatar ]
 *   mobile   [ burger ] [ mark ] ..... [ name ] [ avatar ]
 *
 * The choice is made in JS rather than with `display:none` so that only one
 * arrangement is ever in the DOM — which is what makes the ordering
 * assertable, and what keeps the name from being announced twice.
 *
 * The mockup's notifications bell and design-notes toggle are deliberately
 * absent: the product has no notifications, and the toggle is mockup
 * scaffolding.
 */
function TopBarV2({ mobileNavButton }: TopBarV2Props) {
  return (
    <Container
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: '$3',
        height: '100%',
        px: '$6',
        '@lg': {
          px: '$20',
        },
      }}
    >
      <SmallAndBelow>
        {mobileNavButton}
        {/*
          Decorative: the sidebar's Brand lockup carries the "Zai Payments"
          accessible name, and the sidebar stays in the accessibility tree even
          when translated off-canvas on mobile — it's hidden with CSS
          `transform`, not `display:none` or `inert`. An accessible name here
          would announce the product twice.

          40px, not the 26px this replaced: the official icon occupies 62% of
          its 500x500 viewBox (the file carries its own clearspace) where
          `zai-icon.svg` filled its box edge to edge. 40px puts the mark back at
          ~25px optically.
        */}
        <ZaiMark
          width="40"
          height="40"
          aria-hidden="true"
          focusable="false"
          data-testid="zai-mark"
        />
      </SmallAndBelow>

      <MediumAndAbove>
        <MarketplaceName />
      </MediumAndAbove>

      <Flex
        css={{
          ml: 'auto',
          alignItems: 'center',
          gap: '$3',
          minWidth: 0,
        }}
      >
        <SmallAndBelow>
          <MarketplaceName />
        </SmallAndBelow>
        <UserMenu
          trigger={
            <AvatarButton v2 aria-label="user menu">
              <Icon as={UserIcon} css={{ width: '16px', height: '16px' }} />
            </AvatarButton>
          }
        />
      </Flex>
    </Container>
  );
}

export default TopBarV2;
